/**
 * Who a pricing screen, query, draft, preview or pending request belongs to (batch 8 T13b1 repair, #3034).
 *
 * Everything the management screens hold (cached answers, drafts, a displayed cost preview, a request whose outcome is
 * unknown) belongs to ONE signed-in session of one user in one tenant, with the permissions that session holds at this
 * moment. This file turns the existing `authStore` contracts into that, once:
 *
 *  - `owner` = tenant + user + `sessionFamilyId`. It is the persistent identity of a pending request: it survives a reload and
 *    a token rotation and does not survive a new login (a new login starts a new family), a logout or another tenant or user.
 *    A session restored from before families existed is the single legacy session of that user.
 *  - `epoch` (`sessionEpoch`) is the in-memory generation. It is NOT persisted (a reload of the same session is legitimate);
 *    it only fences work in flight in this page: a request started under one epoch can never report into another.
 *  - permissions are read as primitives (never the stable `hasPermission` function, whose identity does not change when the
 *    user's permissions do) so a downgrade re-renders in the same pass, and they are read from the store again at the moment of
 *    every dispatch and every late result.
 *  - a locked portal is not active: nothing is read or sent while the lock overlay is up.
 */

import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';

export const PRICING_READ_PERMISSION = 'inventory.read';
export const PRICING_MANAGE_PERMISSION = 'inventory.pricing.manage';
export const PRICING_FINANCIAL_PERMISSION = 'reports.view_financial';
export const PRICING_CUSTOMERS_PERMISSION = 'customers.read';
/** The cost-bearing category recalculation needs both. */
export const RECALCULATE_PERMISSIONS: readonly string[] = [PRICING_MANAGE_PERMISSION, PRICING_FINANCIAL_PERMISSION];
export const MANAGE_PERMISSIONS: readonly string[] = [PRICING_MANAGE_PERMISSION];

export const LEGACY_FAMILY = 'legacy-session';

export interface PricingOwner {
  readonly tenantId: string;
  readonly userId: string;
  readonly sessionFamilyId: string;
}

export interface PricingAccess {
  /** Identity of the signed-in session (also while locked). `null`: nobody is signed in. */
  readonly owner: PricingOwner | null;
  /** In-memory generation of the session. */
  readonly epoch: number;
  /** Signed in and not locked: the only state in which anything may be read or sent. */
  readonly active: boolean;
  readonly canRead: boolean;
  readonly canManage: boolean;
  readonly canReadCustomers: boolean;
  readonly canFinancial: boolean;
  /** Cost-bearing preview and apply: manage AND financial. */
  readonly canRecalculate: boolean;
}

export const ownerEquals = (a: PricingOwner | null, b: PricingOwner | null): boolean =>
  a !== null && b !== null && a.tenantId === b.tenantId && a.userId === b.userId && a.sessionFamilyId === b.sessionFamilyId;

/** A string that is equal exactly for equal owners. */
export const ownerId = (owner: PricingOwner): string => JSON.stringify([owner.tenantId, owner.userId, owner.sessionFamilyId]);

type AuthSnapshot = ReturnType<typeof useAuthStore.getState>;

function buildOwner(tenantId: unknown, userId: unknown, family: unknown, authenticated: boolean): PricingOwner | null {
  if (!authenticated) return null;
  if (typeof tenantId !== 'string' || tenantId === '' || typeof userId !== 'string' || userId === '') return null;
  return { tenantId, userId, sessionFamilyId: typeof family === 'string' && family !== '' ? family : LEGACY_FAMILY };
}

function buildAccess(
  owner: PricingOwner | null,
  epoch: number,
  locked: boolean,
  holds: { read: boolean; manage: boolean; customers: boolean; financial: boolean },
): PricingAccess {
  const active = owner !== null && !locked;
  const canManage = active && holds.manage;
  const canFinancial = active && holds.financial;
  return {
    owner,
    epoch,
    active,
    canRead: active && holds.read,
    canManage,
    canReadCustomers: active && holds.customers,
    canFinancial,
    canRecalculate: canManage && canFinancial,
  };
}

/** The access of the store as it is at this moment. Use it at the moment of an action, never a captured copy. */
export function readPricingAccess(state: AuthSnapshot = useAuthStore.getState()): PricingAccess {
  const owner = buildOwner(state.user?.tenantId, state.user?.id, state.sessionFamilyId, state.isAuthenticated);
  return buildAccess(owner, state.sessionEpoch, state.isLocked, {
    read: state.hasPermission(PRICING_READ_PERMISSION),
    manage: state.hasPermission(PRICING_MANAGE_PERMISSION),
    customers: state.hasPermission(PRICING_CUSTOMERS_PERMISSION),
    financial: state.hasPermission(PRICING_FINANCIAL_PERMISSION),
  });
}

/** The reactive access. Every input is a primitive, so an owner or permission change re-renders in the same pass. */
export function usePricingAccess(): PricingAccess {
  const tenantId = useAuthStore((state) => state.user?.tenantId ?? null);
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const family = useAuthStore((state) => state.sessionFamilyId);
  const authenticated = useAuthStore((state) => state.isAuthenticated);
  const locked = useAuthStore((state) => state.isLocked);
  const epoch = useAuthStore((state) => state.sessionEpoch);
  const read = useAuthStore((state) => state.hasPermission(PRICING_READ_PERMISSION));
  const manage = useAuthStore((state) => state.hasPermission(PRICING_MANAGE_PERMISSION));
  const customers = useAuthStore((state) => state.hasPermission(PRICING_CUSTOMERS_PERMISSION));
  const financial = useAuthStore((state) => state.hasPermission(PRICING_FINANCIAL_PERMISSION));
  return useMemo(
    () => buildAccess(buildOwner(tenantId, userId, family, authenticated), epoch, locked, { read, manage, customers, financial }),
    [tenantId, userId, family, authenticated, locked, epoch, read, manage, customers, financial],
  );
}

/**
 * Identity of everything one signed-in owner may see and do: owner, generation and rights. Used as a React `key`, so a
 * different owner, a new login of the same user or any change of rights remounts the subtree in the same render that
 * sees it: no frame shows another owner's drafts, previews or names.
 */
export function accessKey(access: PricingAccess): string {
  if (access.owner === null || !access.active) return 'none';
  return JSON.stringify([
    access.owner.tenantId,
    access.owner.userId,
    access.owner.sessionFamilyId,
    access.epoch,
    access.canRead,
    access.canManage,
    access.canReadCustomers,
    access.canFinancial,
  ]);
}

// ---------------------------------------------------------------------------
// Refusals and fences
// ---------------------------------------------------------------------------

/** Refused before any request: the caller lacks what the endpoint needs. */
export class PricingAccessError extends Error {
  constructor(readonly required: readonly string[]) {
    super(`Missing permission: ${required.join(', ')}`);
    this.name = 'PricingAccessError';
  }
}

/** Refused before any request: the session, tenant, user or generation is no longer the one the action began under. */
export class OwnerChangedError extends Error {
  constructor() {
    super('The session changed before the request was sent');
    this.name = 'OwnerChangedError';
  }
}

export type PricingOutcome<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: unknown };

/**
 * A request WAS sent, and its answer arrived after the session, tenant, user, generation or a needed permission had changed.
 * The answer must not reach the screen, the cache or a notification; it is carried only so the owner of a keyed request can
 * still learn what the server decided.
 */
export class LateResultError extends Error {
  constructor(readonly outcome: PricingOutcome<unknown>) {
    super('The result arrived after the session changed');
    this.name = 'LateResultError';
  }
}

/** A refusal of the client that happened before anything was sent. */
export const isLocalRefusal = (error: unknown): boolean => error instanceof PricingAccessError || error instanceof OwnerChangedError;
/** Errors a screen must swallow silently: they concern an owner that is no longer on screen. */
export const isSessionCancel = (error: unknown): boolean => error instanceof OwnerChangedError || error instanceof LateResultError;

/** What an in-flight action started under, checked again before it may change anything. */
export interface PricingFence {
  readonly owner: PricingOwner;
  readonly epoch: number;
  readonly permissions: readonly string[];
}

/**
 * The fence of NOW, for an action that began under `expected` (the access of the render that offered it). Throws before anything
 * is sent when that owner or generation is not the live one, or when a needed permission is no longer held.
 */
export function takeFence(expected: Pick<PricingAccess, 'owner' | 'epoch'>, permissions: readonly string[]): PricingFence {
  if (expected.owner === null) throw new PricingAccessError(['session']);
  const state = useAuthStore.getState();
  const live = readPricingAccess(state);
  if (!live.active || live.owner === null || !ownerEquals(live.owner, expected.owner) || live.epoch !== expected.epoch) {
    throw new OwnerChangedError();
  }
  const missing = permissions.filter((permission) => !state.hasPermission(permission));
  if (missing.length > 0) throw new PricingAccessError(missing);
  return { owner: live.owner, epoch: live.epoch, permissions };
}

/** Whether what a fence was taken under still holds: same owner, same generation, unlocked, permissions still held. */
export function fenceHolds(fence: PricingFence): boolean {
  const state = useAuthStore.getState();
  const live = readPricingAccess(state);
  return (
    live.active &&
    ownerEquals(live.owner, fence.owner) &&
    live.epoch === fence.epoch &&
    fence.permissions.every((permission) => state.hasPermission(permission))
  );
}

// ---------------------------------------------------------------------------
// Cache scope
// ---------------------------------------------------------------------------

/** What the query keys carry. The family is omitted for the legacy session, so that key stays what it always was. */
export interface PricingScope {
  tenantId: string;
  userId: string;
  sessionFamilyId?: string;
}

export function scopeOfOwner(owner: PricingOwner): PricingScope {
  return owner.sessionFamilyId === LEGACY_FAMILY
    ? { tenantId: owner.tenantId, userId: owner.userId }
    : { tenantId: owner.tenantId, userId: owner.userId, sessionFamilyId: owner.sessionFamilyId };
}
